'use client';

import { useCallback, useState } from 'react';
import { SingSaverRewardsSectionId } from '../cms-blocks/ComparisonTable/constants';
import { useProductBlockAsideModal } from '../components/ProductBlockAsideModal';
import { ProductDetailsModalMode } from '../components/ProductDetails/ProductDetailModalMode';
import { CmsProduct } from '../types/cmsProduct';
import { GetPdpModalConfigAction, RenderPdpModalHero } from '../types/cmsTypes';
import { PushDataLayerFn } from '../types/pushDataLayer';
import { shouldUsePdpModal } from '../utils/helpers/urlAlias';
import useOpenRewardModal from './useOpenRewardModal';

// Callers pass the legacy aside panel's anchor ids; the PDP modal has its own
// section ids, so the intent has to be translated instead of dropped (CMS-1924).
// Anything unmapped opens the modal at the top, as it would in the panel.
const PDP_SECTION_BY_LEGACY_ANCHOR: Record<string, string> = {
  [SingSaverRewardsSectionId]: 'section-offers',
  exclusiveOffers: 'section-offers',
};

interface UseProductDetailModalProps {
  /**
   * Unique per block instance — the rewardModal hash matches opener to
   * renderer by this key, so two blocks sharing a key would open each
   * other's modals.
   */
  componentUniqueKey: string;
  /**
   * App-provided fetcher for the canonical PDP page's benefit filter + the
   * preview flag its cache bypass keys off; forwarded to the modal. fe callers
   * use the app wrapper (`$/components/cms/helpers/useProductDetailModal`),
   * which supplies both — missing action → product-derived fallback (CMS-1924).
   */
  getPdpModalConfigAction?: GetPdpModalConfigAction;
  isPreview?: boolean;
  renderPdpModalHero?: RenderPdpModalHero;
  /**
   * dataLayer sink for the modal's own events (sticky nav, sticky footer Apply — CMS-2043);
   * without it they stay silent. fe callers get it from the app wrapper.
   */
  pushDataLayer?: PushDataLayerFn;
}

/**
 * Single entry point for "open this product's detail modal" (CMS-1924):
 * a CC product (by uid prefix) whose url_alias carries the #usePDP marker
 * opens the ProductDetails modal; everything else keeps the legacy aside panel
 * (alias passed verbatim — it is the panel's product-lookup key).
 * Render the returned `productDetailModal` next to the block.
 */
export const useProductDetailModal = ({
  componentUniqueKey,
  getPdpModalConfigAction,
  isPreview,
  renderPdpModalHero,
  pushDataLayer,
}: UseProductDetailModalProps) => {
  const {
    openModal: openRewardModal,
    rewardModalProductId,
    rewardModalComponentKey,
  } = useOpenRewardModal();
  const { openModal: openProductAside } = useProductBlockAsideModal();
  // Only the clicked product's modal is mounted (a grid may hold dozens of
  // products). Trade-off: a rewardModal hash deep-link won't reopen this
  // modal on a fresh page load — matches how the click-through UX works
  // today; alias deep-links (legacy panel) are unaffected.
  const [pdpTarget, setPdpTarget] = useState<{
    product: CmsProduct;
    idScrollTo?: string;
    ctaLinkOverride?: string;
  } | null>(null);

  // `ctaLinkOverride`: the opener's apply link for the product (a card's or a table row's), which
  // the modal's hero and sticky footer follow; the legacy panel finds its own (CMS-2043).
  const openProductDetail = useCallback(
    (
      product: CmsProduct,
      idScrollTo: string | null = null,
      { ctaLinkOverride }: { ctaLinkOverride?: string } = {},
    ) => {
      // No uid means no rewardModal hash to match on, so the PDP modal could
      // never open — fall through to the panel rather than write a dead hash.
      if (shouldUsePdpModal(product) && product.uid) {
        setPdpTarget({
          product,
          idScrollTo: idScrollTo
            ? PDP_SECTION_BY_LEGACY_ANCHOR[idScrollTo]
            : undefined,
          ctaLinkOverride,
        });
        openRewardModal(product.uid, componentUniqueKey);
        return;
      }
      product.url_alias && openProductAside(product.url_alias, idScrollTo);
    },
    [componentUniqueKey, openRewardModal, openProductAside],
  );

  const productDetailModal = pdpTarget ? (
    <ProductDetailsModalMode
      __typename="ProductDetailsComponent"
      id={componentUniqueKey}
      renderingMode="MODAL"
      product={pdpTarget.product}
      keyFromComponent={componentUniqueKey}
      idScrollTo={pdpTarget.idScrollTo}
      getPdpModalConfigAction={getPdpModalConfigAction}
      isPreview={isPreview}
      renderPdpModalHero={renderPdpModalHero}
      pushDataLayer={pushDataLayer}
      ctaLinkOverride={pdpTarget.ctaLinkOverride}
    />
  ) : null;

  // uid of the product this block currently has open in the PDP modal, for
  // callers that mark the originating row/card as selected. Read off the hash
  // rather than off `pdpTarget`, which outlives the close (CMS-1924).
  const openProductUid =
    rewardModalComponentKey === componentUniqueKey && rewardModalProductId
      ? rewardModalProductId
      : undefined;

  return { openProductDetail, productDetailModal, openProductUid };
};
